import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Divider,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  getNotes,
  addNote,
  toggleNote,
  removeNote,
  editNote,
} from "../services/store/actions";
import { getNotesData } from "../services/store/reducer";

import "../components/Notes.css";

const PAGE_SIZE = 10;

const SectionPager = ({ page, totalPages, onPrev, onNext }) => (
  <div className="notes-pager">
    <IconButton
      size="small"
      onClick={onPrev}
      disabled={page <= 0}
      aria-label="Página anterior"
    >
      <ChevronLeftIcon fontSize="small" />
    </IconButton>

    <span className="notes-pager-text">
      Página {totalPages <= 0 ? "0/0" : `${page + 1}/${totalPages}`}
    </span>

    <IconButton
      size="small"
      onClick={onNext}
      disabled={page >= totalPages - 1}
      aria-label="Página siguiente"
    >
      <ChevronRightIcon fontSize="small" />
    </IconButton>
  </div>
);

const RangeFilter = ({ value, onChange }) => (
  <FormControl variant="outlined" size="small" className="notes-filter">
    <Select value={value} onChange={onChange} label="Rango">
      <MenuItem value="day">Último día</MenuItem>
      <MenuItem value="week">Última semana</MenuItem>
      <MenuItem value="month">Último mes</MenuItem>
      <MenuItem value="3months">3 meses</MenuItem>
      <MenuItem value="all">Todas</MenuItem>
    </Select>
  </FormControl>
);

const NotesContainer = ({
  notes,
  loading,
  getNotes,
  addNote,
  toggleNote,
  removeNote,
  editNote,
}) => {
  const [text, setText] = useState("");
  const [showDone, setShowDone] = useState(false);

  const [noteToDelete, setNoteToDelete] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const [range, setRange] = useState("month"); // day | week | month | 3months | all

  const [pendingPage, setPendingPage] = useState(0);
  const [donePage, setDonePage] = useState(0);

  useEffect(() => {
    getNotes();
  }, [getNotes]);

  // Reset paginación al cambiar rango
  useEffect(() => {
    setPendingPage(0);
    setDonePage(0);
  }, [range]);

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditingText(note.text || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const value = (editingText || "").trim();
    if (!value) return;
    await editNote(editingId, value);
    cancelEdit();
  };

  const openDeleteDialog = (note) => setNoteToDelete(note);
  const closeDeleteDialog = () => setNoteToDelete(null);

  const confirmDelete = async () => {
    if (!noteToDelete) return;
    await removeNote(noteToDelete.id);
    setNoteToDelete(null);
  };

  const handleCreate = async () => {
    const value = (text || "").trim();
    if (!value) return;
    await addNote(value);
    setText("");
  };

  // Filtro global
  const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const rangesMs = {
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    "3months": 90 * 24 * 60 * 60 * 1000,
  };

  const cutoff = useMemo(() => {
    if (range === "all") return 0;
    if (range === "day") return startOfToday();
    return Date.now() - rangesMs[range];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const getRelevantDate = (note) =>
    note.done ? note.completedAt || note.createdAt : note.createdAt;

  const filteredNotes = useMemo(() => {
    return (notes || []).filter((n) => {
      const t = getRelevantDate(n) || 0;
      return range === "all" ? true : t >= cutoff;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, range, cutoff]);

  const pending = useMemo(
    () => filteredNotes.filter((n) => !n.done),
    [filteredNotes]
  );
  const done = useMemo(
    () => filteredNotes.filter((n) => !!n.done),
    [filteredNotes]
  );

  // Paginación
  const paginate = (arr, page) =>
    arr.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const pendingTotalPages = Math.max(1, Math.ceil(pending.length / PAGE_SIZE));
  const doneTotalPages = Math.max(1, Math.ceil(done.length / PAGE_SIZE));

  const pendingPageItems = paginate(pending, pendingPage);
  const donePageItems = paginate(done, donePage);

  useEffect(() => {
    if (pendingPage > pendingTotalPages - 1) {
      setPendingPage(Math.max(0, pendingTotalPages - 1));
    }
  }, [pendingPage, pendingTotalPages]);

  useEffect(() => {
    if (donePage > doneTotalPages - 1) {
      setDonePage(Math.max(0, doneTotalPages - 1));
    }
  }, [donePage, doneTotalPages]);

  const renderItem = (note) => {
    const secondaryText = note.done
      ? note.completedAt
        ? `Finalizada: ${new Date(note.completedAt).toLocaleString()}`
        : note.createdAt
        ? `Creada: ${new Date(note.createdAt).toLocaleString()}`
        : null
      : note.createdAt
      ? `Creada: ${new Date(note.createdAt).toLocaleString()}`
      : null;

    const isEditing = editingId === note.id;

    return (
      <ListItem key={note.id} dense>
        <Checkbox
          checked={!!note.done}
          onChange={() => toggleNote(note.id, note.done)}
        />

        <div style={{ flex: 1 }}>
          {!isEditing ? (
            <ListItemText
              primary={note.text}
              secondary={secondaryText}
              className={note.done ? "notes-done" : ""}
              onClick={() => startEdit(note)}
              style={{ cursor: "text" }}
            />
          ) : (
            <TextField
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              fullWidth
              autoFocus
              onBlur={saveEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                if (e.key === "Escape") cancelEdit();
              }}
            />
          )}
        </div>

        <IconButton
          className="notes-delete-btn"
          onClick={() => openDeleteDialog(note)}
          aria-label="delete"
        >
          <DeleteIcon />
        </IconButton>
      </ListItem>
    );
  };

  return (
    <div className="notes-page">
      <div className="notes-wrapper">
        {/* Añadir nota */}
        <Paper className="notes-card">
          <Typography variant="h5" className="notes-title">
            Notas
          </Typography>

          <div className="notes-add">
            <TextField
              label="Nueva nota"
              value={text}
              onChange={(e) => setText(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <Button variant="contained" color="primary" onClick={handleCreate}>
              Añadir
            </Button>
          </div>
        </Paper>

        {/* Listado */}
        <Paper className="notes-card">
          {/* Pendientes header: filtro izquierda, título centrado, pager derecha */}
          <div className="notes-section-header-grid">
            <RangeFilter value={range} onChange={(e) => setRange(e.target.value)} />

            <Typography variant="subtitle1" className="notes-subtitle">
              Pendientes{" "}
              <span className="notes-count notes-count-pending">
                ({pending.length})
              </span>
            </Typography>

            <SectionPager
              page={pendingPage}
              totalPages={pendingTotalPages}
              onPrev={() => setPendingPage((p) => Math.max(0, p - 1))}
              onNext={() =>
                setPendingPage((p) => Math.min(pendingTotalPages - 1, p + 1))
              }
            />
          </div>

          <List>
            {pendingPageItems.map(renderItem)}
            {pending.length === 0 && !loading && (
              <Typography className="notes-empty">
                No hay notas pendientes
              </Typography>
            )}
          </List>

          <Divider style={{ margin: "12px 0" }} />

          {/* Completadas header: filtro izquierda, centro título+toggle, pager derecha */}
          <div className="notes-section-header-grid">
            <RangeFilter value={range} onChange={(e) => setRange(e.target.value)} />

            <div className="notes-section-center">
              <Typography variant="subtitle1" className="notes-subtitle">
                Completadas{" "}
                <span className="notes-count notes-count-done">
                  ({done.length})
                </span>
              </Typography>

              <Button
                size="small"
                className="notes-toggle-btn"
                onClick={() => setShowDone((v) => !v)}
              >
                {showDone ? "Ocultar" : "Ver"}
              </Button>
            </div>

            <SectionPager
              page={donePage}
              totalPages={doneTotalPages}
              onPrev={() => setDonePage((p) => Math.max(0, p - 1))}
              onNext={() =>
                setDonePage((p) => Math.min(doneTotalPages - 1, p + 1))
              }
            />
          </div>

          <Collapse in={showDone} timeout="auto" unmountOnExit>
            <List>
              {donePageItems.map(renderItem)}
              {done.length === 0 && !loading && (
                <Typography className="notes-empty">
                  Aún no hay notas completadas.
                </Typography>
              )}
            </List>
          </Collapse>
        </Paper>

        {/* Confirmación borrar */}
        <Dialog open={!!noteToDelete} onClose={closeDeleteDialog}>
          <DialogTitle>Eliminar nota</DialogTitle>

          <DialogContent>
            <Typography>¿Seguro que quieres eliminar esta nota?</Typography>
            {noteToDelete && (
              <Typography style={{ marginTop: 8, fontStyle: "italic" }}>
                “{noteToDelete.text}”
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={closeDeleteDialog}>Cancelar</Button>
            <Button onClick={confirmDelete} color="secondary" variant="contained">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  notes: getNotesData(state.notes.data || {}),
  loading: state.notes.loading,
});

export default connect(mapStateToProps, {
  getNotes,
  addNote,
  toggleNote,
  removeNote,
  editNote,
})(NotesContainer);